import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SAFE_BOTTOM } from '../safeArea';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Badge,
  Button,
  IconButton,
  Tabs,
  Tab,
  Skeleton,
  Tooltip,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ExactIcon from '@mui/icons-material/LocationOn';
import NeighborhoodIcon from '@mui/icons-material/LocationCity';
import CityIcon from '@mui/icons-material/Public';
import HiddenIcon from '@mui/icons-material/LocationOff';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SellIcon from '@mui/icons-material/Sell';
import VerifiedIcon from '@mui/icons-material/Verified';
import { formatDistance, calculateDistance } from '../services/LocationService';
import { generatedAvatarUrl, collectorDisplayName } from '../collectorIdentity';
import ROUTES from '../../router/routesModel';
import { getUserCollection } from '../../collection/collectionApi';
import { getOrCreateThread } from '../../chat/chatApi';
import { CONDITION_COLORS, conditionLabel } from '../../utils/cardPricing';
import ChallengeReviewedBadge from '../../cards/components/ChallengeReviewedBadge';
import { FEATURE_FLAGS } from '../../utils/featureFlags';
import { useCurrency } from '../../providers/currencyContext';
// An asking price is entered in the source currency and stored untagged, so it
// is rendered with that code explicitly rather than by defaulting.
import { SOURCE_CURRENCY } from '../../utils/currency';
import { prefersReducedMotion } from '../../styles/particleField';
import { useDismissible } from '../../native/dismissibleStack';
import { claimRefusals } from '../../utils/refusalNotice';

const isValidMongoId = (id) => typeof id === 'string' && /^[a-fA-F0-9]{24}$/.test(id);

/**
 * A usable {latitude, longitude}, or null.
 *
 * Two shapes reach this sheet: map users carry `[lng, lat]` (both MapPage
 * normalizers and MarketplaceView build that), and the viewer's point arrives
 * from MapPage's `originCoordinates` memo as `{longitude, latitude}`.
 *
 * The finiteness check is not decoration. `normalizeApiUser` sets
 * `coordinates: u.coordinates || []`, and an empty array is truthy — so a
 * collector the server sent no position for used to sail through a
 * `coords && coords` guard, reach the haversine as `undefined`, and render
 * "NaNkm". That was unreachable only because the viewer's half of the guard
 * never passed either; fixing that half makes it reachable, so it is closed here.
 */
const toGeoPoint = (coords) => {
  if (!coords) return null;
  const latitude = Array.isArray(coords) ? coords[1] : coords.latitude;
  const longitude = Array.isArray(coords) ? coords[0] : coords.longitude;
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? { latitude, longitude }
    : null;
};

// Auctions aren't live yet. When they launch, bidding lives on the card itself
// (the per-card "Bid" button below), never as a profile-level action. Until then
// we hide it behind FEATURE_FLAGS.ENABLE_BIDDING (#84); the server enforces the
// same boundary fail-closed, so flipping this alone exposes nothing.

// The Vault is no longer a tab — it is a permanently-expanded showcase above the
// tabs, matching the public profile. Only the two *actionable* buckets are
// tabbed: what you can trade for and what you can buy.
const TABS = [
  { key: 'trade', label: 'For Trade', Icon: SwapHorizIcon, showPrice: false },
  { key: 'sale', label: 'For Sale', Icon: SellIcon, showPrice: true },
];

const EMPTY_COPY = {
  trade: "Nothing up for trade right now.",
  sale: "Nothing listed for sale right now.",
};

/**
 * A card-shaped last resort when a card's art fails to load.
 *
 * Cards used to share the avatar's onError, so a card with a broken image
 * became a ui-avatars initials circle — a round grey bubble sitting in a grid
 * of cards, which reads as a rendering bug rather than a missing image.
 */
const CARD_FALLBACK_IMG = 'https://placehold.co/200x280?text=Card';

/**
 * Step down the image chain one rung at a time: owner photo -> this card's own
 * catalog art -> the card-shaped placeholder.
 *
 * The middle rung is the point, and it is #252's rule rather than a new one:
 * an owner photo is hosted somewhere else entirely and can 404 while the
 * catalogue art is perfectly fine, and the honest substitute is THIS card's
 * official art, not a generic stand-in that would put another card under this
 * card's name and condition chip. Before Vault tiles preferred the owner photo
 * the chain had only one rung, so falling straight to the placeholder was the
 * whole of it.
 *
 * The rung is tracked in a data attribute rather than by comparing
 * `e.target.src` to the candidate: the DOM reports a RESOLVED absolute URL, so
 * a relative candidate never compares equal and the handler would loop.
 */
const handleCardImageError = (e, catalogSrc) => {
  const stage = e.target.dataset.imgStage;
  if (stage === "owner" && catalogSrc) {
    e.target.dataset.imgStage = "catalog";
    e.target.src = catalogSrc;
    return;
  }
  if (stage === "placeholder") return;
  e.target.dataset.imgStage = "placeholder";
  e.target.src = CARD_FALLBACK_IMG;
};

/** The response can arrive partial; every bucket must still be an array. */
const normaliseCollection = (data) => ({
  glory: Array.isArray(data?.glory) ? data.glory : [],
  trade: Array.isArray(data?.trade) ? data.trade : [],
  sale: Array.isArray(data?.sale) ? data.sale : [],
});

const cardKey = (item) => item._id || (item.cardId || item).id;

/**
 * CardTile — one card.
 *
 * Module scope on purpose. Declared inside the parent it was a brand-new
 * component type on every render, so React unmounted and remounted every tile
 * (and re-downloaded every image) on any state change, including a tab switch.
 *
 * `preferOwnerPhoto` is passed by the Vault strip only. The Vault uploader in
 * `collection/ListCardModal.jsx` tells the owner their photo "becomes this
 * card's image in your Vault — the one other collectors see on your profile",
 * and this sheet is one of the two surfaces that sentence is about. It drew
 * catalog art regardless. The trade/sale tabs are deliberately left alone:
 * their copy frames photos as a condition disclosure, not as the tile image.
 *
 * Read `item.photos`, never `card.photos`. `card` is USUALLY the populated
 * catalog document, which carries no owner photos — but the line below is
 * `item.cardId || item`, so on the unpopulated fallback `card` IS the row and
 * `card.photos` would work by accident. Reading `item` is the only spelling
 * that is correct on both branches.
 */
const CardTile = ({ item, showPrice = false, onBidClick = null, formatAmount, sx, preferOwnerPhoto = false }) => {
  const card = item.cardId || item;
  const catalogSrc = card.images?.small || card.imageUrl || null;
  const ownerSrc = preferOwnerPhoto ? item.photos?.[0]?.url : null;
  const img = ownerSrc || catalogSrc || CARD_FALLBACK_IMG;
  const showBid = onBidClick && showPrice && item.askingPrice > 0;
  // Read at render, never at module scope: the preference can change while the
  // app is open, and a value captured at import time could never notice.
  const reduced = prefersReducedMotion();
  return (
    <Box
      data-testid="collector-card-tile"
      sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, ...sx }}
    >
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '5 / 7',
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: 'grey.100',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          // The press-in still happens — the tile is the only confirmation a
          // tap landed, so it keeps scaling, it just snaps instead of easing.
          transition: reduced ? 'none' : 'transform 0.15s',
          '&:active': { transform: 'scale(0.97)' },
        }}
      >
        <img
          src={img}
          alt={card.name || 'Card'}
          loading="lazy"
          data-img-stage={ownerSrc ? 'owner' : 'catalog'}
          onError={(e) => handleCardImageError(e, catalogSrc)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {item.condition && (
          <Chip
            label={conditionLabel(item.condition)}
            size="small"
            sx={{ position: 'absolute', top: 4, left: 4, height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: CONDITION_COLORS[item.condition] || '#999', color: '#fff' }}
          />
        )}
        {showPrice && item.askingPrice > 0 && (
          <Chip
            label={formatAmount(item.askingPrice, SOURCE_CURRENCY)}
            size="small"
            sx={{ position: 'absolute', bottom: 4, right: 4, height: 18, fontSize: '0.62rem', fontWeight: 800, bgcolor: 'success.main', color: '#fff' }}
          />
        )}
      </Box>
      <Typography variant="caption" noWrap sx={{ mt: 0.5, fontWeight: 600, lineHeight: 1.2 }}>
        {card.name || 'Card'}
      </Typography>
      {/*
        `item` is the other collector's UserCard, straight from
        getUserCollection — so the published badge arrives with the listing and
        needs no extra request.
      */}
      {item.challengeReviewedAt && (
        <Box sx={{ mt: 0.3 }}>
          <ChallengeReviewedBadge reviewedAt={item.challengeReviewedAt} variant="compact" />
        </Box>
      )}
      {showBid && (
        <Button
          size="small"
          variant="outlined"
          sx={{ mt: 0.3, height: 22, fontSize: '0.6rem', py: 0, minWidth: 0 }}
          onClick={(e) => { e.stopPropagation(); onBidClick(item); }}
        >
          Place bid
        </Button>
      )}
    </Box>
  );
};

/**
 * UserSidebar — the collector card that opens from a map pin.
 *
 * Card-first (senior UX pass): the old layout spent ~260px on a centred avatar
 * header and ~180px on three stacked full-width buttons, which squeezed the
 * actual cards into a single cut-off row. Now a compact header and a slim
 * sticky action bar bracket a DOMINANT, scrollable card grid.
 *
 * The Vault sits above the tabs as its own showcase, the way the public profile
 * shows it. Buried as the third and lowest-priority tab, a collector's prized
 * cards — the ones that exist *only* to be shown off — were the hardest thing
 * in the sheet to see. It scrolls horizontally rather than filling a 3-up grid,
 * because the sheet's vertical space belongs to the tabbed grid; a showcase
 * that pushes the grid off-screen defeats its own purpose. When the Vault is
 * empty it renders nothing at all — an empty showcase is dead chrome.
 */
const UserSidebar = ({
  currentUser,
  selectedUser,
  onClose,
  isMobile,
  onTradeRequest,
  onBid,
  isRoaming = false,
  // P2-46 — the viewer's own point, from the map's live geolocation. It is a
  // separate prop because `currentUser` is an IDENTITY object
  // ({id,name,email,avatar}) and never carried coordinates, which is why the
  // distance line below could never render. Null when location is off or not
  // resolved yet; the sheet then shows the precision line alone.
  originCoordinates = null,
}) => {
  const navigate = useNavigate();

  // Android hardware Back closes this sheet instead of leaving the app. Without
  // it the press fell through to the `/map` home branch, showed "press back
  // again to exit", and a second reflexive press quit the app from inside an
  // open collector profile.
  //
  // Hard-coded `true` rather than an `open` prop, the same shape (and for the
  // same reason) as LocationPrivacySettings: this component has no `open` prop,
  // MapPage mounts it inside a drawer and unmounts it on close, so being
  // mounted IS being open. That last clause is the load-bearing part and it is
  // held up by exactly one prop — the SwipeableDrawer's
  // `ModalProps={{ keepMounted: false }}`, because MUI merges its own
  // `keepMounted: true` for the temporary variant first. Delete that prop and
  // this sheet stays mounted for the whole session, which would leave it
  // claiming Back forever and make Back a dead key on the map.
  //
  // Above the `if (!user)` early return below, because hooks cannot sit under
  // one — and unconditional rather than gated on `user`, because the empty
  // "No collector selected" state is still a sheet on screen that Back must
  // close.
  useDismissible(true, onClose, 'collector-sheet');

  const { formatAmount } = useCurrency();
  const [activeTab, setActiveTab] = useState(0);
  const [collection, setCollection] = useState(() => normaliseCollection());
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const user = selectedUser || currentUser;

  // Map payloads carry `_id`; the auth store carries `id`. Comparing one field
  // meant the sheet showed a stranger's Trade/Chat buttons on your own pin.
  const currentUserId = currentUser?._id || currentUser?.id;
  const viewedUserId = user?._id || user?.id;
  const isCurrentUser = !!(
    currentUserId && viewedUserId && String(currentUserId) === String(viewedUserId)
  );

  // Reset the "pick the first non-empty tab once" guard whenever the profile
  // changes, so each collector opens on a tab that actually has cards.
  const didAutoSelectRef = useRef(false);
  useEffect(() => {
    didAutoSelectRef.current = false;
    setActiveTab(0);
  }, [user?._id, user?.id]);

  useEffect(() => {
    const userId = user?._id || user?.id;
    if (!userId) return;
    if (!isValidMongoId(userId)) {
      setCollection(normaliseCollection());
      return;
    }
    setCollectionLoading(true);
    getUserCollection(userId)
      .then((data) => setCollection(normaliseCollection(data)))
      .catch(() => setCollection(normaliseCollection()))
      .finally(() => setCollectionLoading(false));
  }, [user?._id, user?.id]);

  // Open on the first tab that has cards (Trade → Sale), so the sheet never
  // greets you with an empty grid when the collector has cards in the other one.
  useEffect(() => {
    if (collectionLoading || didAutoSelectRef.current) return;
    const counts = TABS.map((t) => (collection[t.key] || []).length);
    const firstNonEmpty = counts.findIndex((c) => c > 0);
    if (firstNonEmpty >= 0) {
      setActiveTab(firstNonEmpty);
      didAutoSelectRef.current = true;
    }
  }, [collectionLoading, collection]);

  const getLocationIcon = (precision) => {
    switch (precision) {
      case 'Exact': return <ExactIcon color="success" fontSize="small" />;
      // 'Approximate' is what the map's own normalizer stamps on every nearby
      // collector, and it is not in LOCATION_PRECISION — so it fell through to
      // the red 'location hidden' glyph, and the sheet claimed a collector's
      // location was withheld while the map was drawing their pin. A fuzzed
      // public point IS neighbourhood-grade; it reads as one now.
      case 'Approximate':
      case 'Neighborhood': return <NeighborhoodIcon color="info" fontSize="small" />;
      case 'City': return <CityIcon color="warning" fontSize="small" />;
      default: return <HiddenIcon color="error" fontSize="small" />;
    }
  };

  const formatLastActive = (lastActive) => {
    const diffMs = Date.now() - new Date(lastActive).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  /**
   * Avatar-only fallback: initials are the right shape for a person.
   *
   * The `!==` guard is not defensive decoration. Re-assigning an identical
   * `data:` URI that the browser cannot decode fires `onerror` AGAIN, so
   * without it a fallback that itself fails to render becomes an unbounded
   * loop in the viewer's browser — measured in real headless Chrome, where the
   * unguarded shape hit every probe cap and the guarded shape stopped at one
   * event. CollectorAvatarMarker, CollectorClusterMarker and
   * CollectorPreviewSheet already guarded. This site and MapView's
   * `.wmp-avatar` <img> did not; both are guarded as of this commit. Two
   * refute-phase skeptics independently caught that MapView one being left
   * out while an earlier draft of this very comment asserted it did not
   * exist — and it is the ONLY bare <img> of the five, so it is the one
   * site where the loop can actually run rather than being absorbed by a
   * MUI Avatar. It was not a regression: the pre-fix handler assigned a
   * remote ui-avatars URL and looped identically.
   */
  const handleAvatarError = (e) => {
    const fallback = generatedAvatarUrl(e.target.alt || 'User');
    if (e.target.src !== fallback) e.target.src = fallback;
  };

  const handleViewProfile = () => {
    const userId = user?._id || user?.id;
    if (userId) {
      navigate(`${ROUTES.PROFILE}/${userId}`);
      if (onClose) onClose();
    }
  };

  const handleStartChat = async () => {
    const userId = user?._id || user?.id;
    if (!userId) return;
    setChatLoading(true);
    try {
      const thread = await getOrCreateThread(userId);
      navigate(`${ROUTES.CHAT}?threadId=${thread._id}`);
      if (onClose) onClose();
    } catch {
      // The chat screen asks again and shows its own answer (B-31).
      claimRefusals();
      navigate(`${ROUTES.CHAT}?userId=${userId}`);
      if (onClose) onClose();
    } finally {
      setChatLoading(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">No collector selected</Typography>
        <Typography variant="body2" color="text.secondary">
          Tap a pin on the map to see their cards.
        </Typography>
      </Box>
    );
  }

  const calculateUserDistance = () => {
    // `originCoordinates` first: that is the map's live fix, and the only
    // viewer point that actually exists in practice. `currentUser.coordinates`
    // stays as a fallback so a caller that does carry one still works.
    const origin = toGeoPoint(originCoordinates) || toGeoPoint(currentUser?.coordinates);
    const target = toGeoPoint(selectedUser?.coordinates);
    if (!origin || !target) return null;
    try {
      return formatDistance(calculateDistance(origin, target));
    } catch (_) { return null; }
  };

  const distance = calculateUserDistance();
  const displayName = collectorDisplayName(user);
  const isOnline = user.onlineStatus === 'Online';
  const statusColor = isOnline ? 'success.main' : user.onlineStatus === 'Away' ? 'warning.main' : 'grey.500';
  // The server field is verification.verified, flattened to a top-level
  // `verified` on map payloads. `verification.isVerified` never existed, so the
  // badge never rendered for anyone.
  const isVerified = !!(user.verified || user.verification?.verified || user.isVerified);
  const vaultCards = collection.glory;
  const tradeCount = collection.trade.length;
  const saleCount = collection.sale.length;
  const vaultCount = vaultCards.length;
  const totalCards = tradeCount + saleCount + vaultCount;
  // Hidden while loading too: showing the header over the *previous*
  // collector's cards, or over a strip that is about to vanish, is worse than
  // showing nothing for a beat.
  const showVault = !collectionLoading && vaultCount > 0;

  const renderCardGrid = (cards, { showPrice = false, onBidClick = null, emptyKey } = {}) => {
    if (collectionLoading) {
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" sx={{ width: '100%', aspectRatio: '5 / 7' }} />
          ))}
        </Box>
      );
    }
    if (!cards || cards.length === 0) {
      return (
        <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">{EMPTY_COPY[emptyKey] || 'No cards here yet.'}</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
        {cards.map((item) => (
          <CardTile
            key={cardKey(item)}
            item={item}
            showPrice={showPrice}
            onBidClick={onBidClick}
            formatAmount={formatAmount}
          />
        ))}
      </Box>
    );
  };

  const activeKey = TABS[activeTab]?.key || 'trade';
  const activeShowPrice = TABS[activeTab]?.showPrice;

  return (
    <Box data-testid="user-sidebar" sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Compact header — a single horizontal row, freeing the vertical space the
          old centred column spent so the cards below can take the sheet. */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={<Box component="span" sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: statusColor, border: '2px solid #fff' }} />}
        >
          <Avatar
            src={user.profilePicture || user.image?.url}
            alt={displayName}
            sx={{ width: 56, height: 56, boxShadow: 2 }}
            imgProps={{ onError: handleAvatarError }}
          />
        </Badge>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              <bdi>{displayName}</bdi>
            </Typography>
            {isVerified && <VerifiedIcon color="primary" sx={{ fontSize: 18, flexShrink: 0 }} titleAccess="Verified collector" />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, color: 'text.secondary', minWidth: 0 }}>
            {getLocationIcon(user.locationPrecision)}
            <Typography variant="caption" noWrap>{user.locationPrecision || 'Approximate'} location</Typography>
            {distance && (
              <>
                <Box component="span" sx={{ mx: 0.25 }}>·</Box>
                <Typography
                  data-testid="collector-distance"
                  variant="caption"
                  noWrap
                  sx={{ fontWeight: 700, color: 'primary.main' }}
                >
                  {distance}
                </Typography>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <FiberManualRecordIcon sx={{ fontSize: 9, color: statusColor }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {user.onlineStatus || 'Offline'}
              {!isOnline && user.lastActive ? ` · ${formatLastActive(user.lastActive)}` : ''}
              {totalCards > 0 ? ` · ${totalCards} card${totalCards === 1 ? '' : 's'}` : ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Vault showcase — the show-off shelf, always expanded, never a tab.
          flexShrink: 0 keeps it a fixed band; the strip scrolls sideways so it
          can never grow tall enough to squeeze the grid below it. */}
      {showVault && (
        <Box
          data-testid="vault-showcase"
          sx={{
            flexShrink: 0,
            mx: 2,
            mb: 1,
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #fef9e7 0%, #fdebd0 100%)',
            border: '1px solid #f0c27f55',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <EmojiEventsIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#92400e' }}>
              Vault
            </Typography>
            <Chip
              label={vaultCount}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#f59e0b', color: '#fff' }}
            />
          </Box>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25, mb: 0.75 }}>
            Prized cards on display — not for sale or trade
          </Typography>
          <Box
            className="hide-scrollbar"
            sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.25 }}
          >
            {vaultCards.map((item) => (
              <CardTile
                key={cardKey(item)}
                item={item}
                formatAmount={formatAmount}
                preferOwnerPhoto
                sx={{ width: 68, flexShrink: 0 }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Count-bearing tabs for the two actionable buckets — both are card
          grids, and the counts tell you where to look before you tap. */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{ minHeight: 44, '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: '0.8rem', minHeight: 44, px: 0.5 } }}
        >
          {TABS.map((t, i) => {
            const count = collection[t.key].length;
            return (
              <Tab
                key={t.key}
                value={i}
                icon={<t.Icon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`${t.label} ${collectionLoading ? '' : `(${count})`}`.trim()}
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Dominant, scrollable card area. minHeight:0 lets this flex child scroll
          instead of pushing the action bar off-screen. */}
      <Box data-testid="collector-cards" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1.5 }}>
        {renderCardGrid(collection[activeKey], {
          showPrice: activeShowPrice,
          onBidClick: activeKey === 'sale' && FEATURE_FLAGS.ENABLE_BIDDING ? onBid : null,
          emptyKey: activeKey,
        })}
      </Box>

      {/* Slim sticky action bar — one row instead of three stacked buttons.

          The bottom padding carries the system inset ON TOP of the 10px design
          padding, because this bar is the last child of the sheet and its host
          puts the sheet's bottom edge at the bottom of the window. That host is
          always MapPage's bottom-anchored SwipeableDrawer: `isMobile` is
          `useMediaQuery(theme.breakpoints.down('sm'))` and `styles/theme.js`
          pushes `sm` out to 100000, so the query is `max-width: 99999.95px` and
          is true at EVERY real viewport — desktop web included. The
          `anchor="left"` Drawer beside it is reachable only in jsdom, which has
          no `matchMedia` and so answers `false`; MapPage's own comment says as
          much. Nothing sits between this bar and the window edge.
          Under Android edge-to-edge the gesture bar is drawn over that edge, so
          without the inset term the Trade button — the map's primary conversion
          action — sits under the navigation bar and takes gesture taps instead.
          Additive `calc()`, not `max()`: `max(10px, inset)` would return the
          inset alone on any device whose bar is taller than 10px, leaving the
          buttons flush against it with the design padding silently deleted. */}
      <Box
        data-testid="collector-action-bar"
        sx={{
          flexShrink: 0,
          p: 1.25,
          pb: `calc(10px + ${SAFE_BOTTOM})`,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          alignItems: 'center',
        }}
      >
        {!isCurrentUser ? (
          <>
            <Button
              variant="contained"
              startIcon={<SwapHorizIcon />}
              onClick={() => (onTradeRequest ? onTradeRequest(user) : null)}
              sx={{ flex: 1.4, fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              Trade
            </Button>
            <Button
              variant="outlined"
              startIcon={<MessageIcon />}
              onClick={handleStartChat}
              disabled={chatLoading}
              sx={{ flex: 1, fontWeight: 700 }}
            >
              {chatLoading ? '…' : 'Chat'}
            </Button>
            <Tooltip title="View full profile">
              <IconButton onClick={handleViewProfile} aria-label="View full profile" sx={{ border: 1, borderColor: 'divider' }}>
                <PersonIcon />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Button variant="outlined" startIcon={<PersonIcon />} fullWidth onClick={handleViewProfile}>
            View my full profile
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default UserSidebar;
